<template>
  <div id="recaptcha" class="g-recaptcha" />
</template>

<script lang="ts" setup>
import { onMounted, reactive } from 'vue'

const props = defineProps({
  verifiedCallback: {
    type: Function,
    required: true
  }
})

const emit = defineEmits<{
  (e: 'loaded', val: boolean): void
  // Distinct from 'loaded': fires when the script never becomes usable
  // (blocked by an ad/content blocker, network error, or execute() missing
  // when we try to call it). Never reuse 'loaded: false' for this - that
  // value already means "still loading" and the parent can't tell the two
  // apart.
  (e: 'failed'): void
}>()

const state = reactive({
  recaptchaId: null as unknown as string
})

onMounted(() => {
  window.grecaptchaApiLoaded = recaptchaApiLoaded
  const script = document.createElement('script')
  script.src =
    'https://www.google.com/recaptcha/api.js?onload=grecaptchaApiLoaded&render=explicit'
  script.id = 'grecaptcha'
  script.onerror = () => {
    emit('failed')
  }
  document.body.appendChild(script)
})

function recaptchaApiLoaded() {
  state.recaptchaId = window.grecaptcha.render('recaptcha', {
    sitekey: '6Ld1i_IUAAAAAPpTS6-MQWIcTgUcWvq5TBuQ3a3R',
    callback: verified,
    size: 'invisible'
  })
  emit('loaded', true)
}

// Returns true when execute() was actually called, false when the API
// isn't ready yet (blocked script, or the narrow window before Google's
// own bootstrap attaches execute). Never throws - the caller decides how
// to surface the failure instead of us guessing at UI behavior.
function verify(): boolean {
  if (typeof window.grecaptcha?.execute !== 'function') {
    emit('failed')
    return false
  }

  window.grecaptcha.execute()
  return true
}

defineExpose({ verify })

function verified(token: string) {
  props.verifiedCallback(token)
  window.grecaptcha.reset(state.recaptchaId)
}
</script>
